import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageContentWrapper } from '../main/LayoutStyles';
import { Card, AlertBox, Tag, StyledButton } from '../main/CommonStyles';
import apiClient from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Loader2, AlertTriangle, User, Briefcase, Info, MapPin, Phone, Globe, CheckCircle } from 'lucide-react';

const FullWidthLayout = styled.div`
    width: 100%;
`;

const ProfileHeader = styled(Card)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 2rem;
    padding: 2rem;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-start;
    }
`;

const ProfileAvatar = styled.img`
    width: 150px;
    height: 150px;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    flex-shrink: 0;
`;

const PlaceholderAvatar = styled.div`
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.neutralLight};
    display: flex;
    align-items: center;
    justify-content: center;
    border: 4px solid ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    flex-shrink: 0;
`;

const ProfileInfo = styled.div`
    text-align: center;
    flex-grow: 1;
    width: 100%; /* Aby akcje mogły się rozciągnąć */
    @media (min-width: 768px) {
        text-align: left;
    }
`;

const ProfileName = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primaryDarker};
    margin: 0;
`;

const ProfileTitle = styled.p`
    font-size: 1.1rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textMedium};
    margin-top: 0.25rem;
`;

const ProfileDetailsCard = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: 2rem;
`;

const DetailsSection = styled.div`
    h4 {
        font-size: 1.25rem;
        color: ${({ theme }) => theme.colors.primaryDark};
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    }
    p, div {
        line-height: 1.7;
        color: ${({ theme }) => theme.colors.textDark};
    }
`;

const TagContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const ContactDetail = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.colors.textDark};
    a {
        color: ${({ theme }) => theme.colors.primary};
        text-decoration: none;
        &:hover { text-decoration: underline; }
    }
`;

// ZMIANA: Nowy kontener na akcje admina, umieszczany w nagłówku
const AdminHeaderActions = styled.div`
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
    text-align: center;

    p {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.danger};
        margin-bottom: 1rem;
    }

    button {
        padding: 0.75rem 1.5rem;
        font-size: 1rem;
    }
`;


const PublicProfileView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isApproving, setIsApproving] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await apiClient.get(`/psychologists/${id}`);
                setProfile(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'Nie udało się załadować profilu.');
            } finally {
                setLoading(false);
            }
        };
        if (id) fetchProfile();
    }, [id]);

    const handleApprove = async () => {
        setIsApproving(true);
        try {
            await apiClient.post(`/admin/psychologists/${id}/approve`);
            navigate('/app/admin-panel/pending-profiles');
        } catch (err) {
            setError("Nie udało się zatwierdzić profilu.");
        } finally {
            setIsApproving(false);
        }
    };

    if (loading) return <PageContentWrapper style={{ textAlign: 'center', paddingTop: '50px' }}><Loader2 size={48} className="icon-spin" /> <p>Wczytywanie profilu...</p></PageContentWrapper>;
    if (error) return <PageContentWrapper><AlertBox type="error"><AlertTriangle /> {error}</AlertBox></PageContentWrapper>;
    if (!profile) return null;

    const specializations = profile.specializations ? profile.specializations.split(',').map(s => s.trim()).filter(Boolean) : [];
    const therapyApproaches = profile.therapyApproaches ? profile.therapyApproaches.split(',').map(s => s.trim()).filter(Boolean) : [];

    return (
        <PageContentWrapper className="custom-scrollbar">
            <FullWidthLayout>
                <ProfileHeader>
                    {profile.userImageUrl ? <ProfileAvatar src={profile.userImageUrl} alt={`${profile.firstName} ${profile.lastName}`} /> : <PlaceholderAvatar><User size={80} color="#cbd5e1" /></PlaceholderAvatar>}
                    <ProfileInfo>
                        <ProfileName>{`${profile.firstName} ${profile.lastName}`}</ProfileName>
                        <ProfileTitle>{profile.professionalTitle}</ProfileTitle>

                        {/* ZMIANA: Przycisk akceptacji przeniesiony tutaj */}
                        {isAdmin && profile.status === 'PENDING_APPROVAL' && (
                            <AdminHeaderActions>
                                <p>Ten profil oczekuje na Twoją akceptację.</p>
                                <StyledButton $variant="secondary" onClick={handleApprove} disabled={isApproving}>
                                    {isApproving ? <Loader2 className="icon-spin" /> : <CheckCircle size={20} />}
                                    {isApproving ? 'Zatwierdzanie...' : 'Zatwierdź i Opublikuj Profil'}
                                </StyledButton>
                            </AdminHeaderActions>
                        )}
                    </ProfileInfo>
                </ProfileHeader>

                <ProfileDetailsCard>
                    {profile.bio && (
                        <DetailsSection>
                            <h4><Info size={22} /> O mnie</h4>
                            <p>{profile.bio}</p>
                        </DetailsSection>
                    )}
                    {specializations.length > 0 && (
                        <DetailsSection>
                            <h4><Briefcase size={22} /> Specjalizacje</h4>
                            <TagContainer>{specializations.map(spec => <Tag key={spec}>{spec}</Tag>)}</TagContainer>
                        </DetailsSection>
                    )}
                    {therapyApproaches.length > 0 && (
                        <DetailsSection>
                            <h4><Briefcase size={22} /> Nurty terapeutyczne</h4>
                            <TagContainer>{therapyApproaches.map(appr => <Tag key={appr} color="#e0f2fe" textColor="#0c4a6e">{appr}</Tag>)}</TagContainer>
                        </DetailsSection>
                    )}
                    <DetailsSection>
                        <h4><MapPin size={22} /> Kontakt i Lokalizacja</h4>
                        {profile.officeAddress && <ContactDetail><MapPin size={18} /> {profile.officeAddress}</ContactDetail>}
                        {profile.contactPhoneNumber && <ContactDetail><Phone size={18} /> <a href={`tel:${profile.contactPhoneNumber}`}>{profile.contactPhoneNumber}</a></ContactDetail>}
                        {profile.websiteUrl && <ContactDetail><Globe size={18} /> <a href={profile.websiteUrl} target="_blank" rel="noopener noreferrer">{profile.websiteUrl}</a></ContactDetail>}
                    </DetailsSection>
                </ProfileDetailsCard>
            </FullWidthLayout>
        </PageContentWrapper>
    );
};

export default PublicProfileView;